import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';

interface SubtypeOption {
  key: string; // canonical key
  label: string; // local label when available, canonical label otherwise
  description: string;
  form_count: number;
  template_id: string | null;
  template_status: string | null;
  // Section 4 — optional taxonomy metadata returned by the backend when the
  // resolver-backed path served the response. Existing consumers that only
  // read the six fields above continue to work.
  canonical_key?: string;
  mapping_id?: string | null;
  local_key?: string | null;
  local_label?: string | null;
  source_scope?: 'court' | 'state' | 'national' | 'legacy' | 'json';
  resolution_status?: 'validated' | 'legacy_fallback' | 'json_fallback';
  warnings?: string[];
}

/**
 * Optional metadata passed to `onChange` when the selected option carries
 * subtype taxonomy information. Backward-compatible: the existing two-arg
 * call shape still works.
 */
export interface SubtypeSelectionMetadata {
  canonical_key?: string;
  mapping_id?: string | null;
  local_key?: string | null;
  local_label?: string | null;
  source_scope?: 'court' | 'state' | 'national' | 'legacy' | 'json';
  resolution_status?: 'validated' | 'legacy_fallback' | 'json_fallback';
}

interface SubtypePickerProps {
  practiceArea: string;
  jurisdictionCode?: string;
  value?: string;
  onChange: (subtypeKey: string, templateId?: string, metadata?: SubtypeSelectionMetadata) => void;
}

const SubtypePicker: React.FC<SubtypePickerProps> = ({
  practiceArea,
  jurisdictionCode,
  value,
  onChange,
}) => {
  const { keycloak } = useKeycloak();
  const [subtypes, setSubtypes] = useState<SubtypeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchSubtypes = useCallback(async () => {
    const token = keycloak?.token;
    if (!token || !practiceArea) {
      setSubtypes([]);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('practice_area', practiceArea);
      if (jurisdictionCode) params.set('jurisdiction_code', jurisdictionCode);

      const response = await fetch(`/api/v1/task-templates/subtypes?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        setSubtypes([]);
        return;
      }

      const data = await response.json();
      if (mountedRef.current && Array.isArray(data?.subtypes)) {
        setSubtypes(data.subtypes);
      }
    } catch {
      if (mountedRef.current) setSubtypes([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [keycloak?.token, practiceArea, jurisdictionCode]);

  useEffect(() => {
    fetchSubtypes();
  }, [fetchSubtypes]);

  if (subtypes.length === 0 && !loading) return null;

  const selected = subtypes.find((s) => s.key === value);

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Case Subtype
      </label>
      <select
        value={value || ''}
        onChange={(e) => {
          const key = e.target.value;
          const match = subtypes.find((s) => s.key === key);
          const metadata: SubtypeSelectionMetadata | undefined = match
            ? {
                canonical_key: match.canonical_key ?? match.key,
                mapping_id: match.mapping_id ?? null,
                local_key: match.local_key ?? null,
                local_label: match.local_label ?? null,
                source_scope: match.source_scope,
                resolution_status: match.resolution_status,
              }
            : undefined;
          onChange(key, match?.template_id || undefined, metadata);
        }}
        disabled={loading}
        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
      >
        <option value="">
          {loading ? 'Loading subtypes...' : 'Select a case subtype (optional)'}
        </option>
        {subtypes.map((subtype) => {
          // Nationals are cross-state canonicals — tag them so the user knows
          // this isn't their jurisdiction's letter-for-letter term.
          const genericSuffix = subtype.source_scope === 'national' ? ' — Generic' : '';
          return (
            <option key={subtype.key} value={subtype.key}>
              {subtype.label} ({subtype.form_count} forms){genericSuffix}
              {subtype.template_id ? ' — Template ready' : ''}
            </option>
          );
        })}
      </select>
      {selected?.description && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{selected.description}</p>
      )}
    </div>
  );
};

export default SubtypePicker;
